import type {
  CaseRequestType,
  CaseStatusGroup,
  PaymentGroup,
  PurchaseRequestStatus,
  StageGroup,
} from '@/lib/api-types'

/**
 * Single source of truth for status → client-facing label + chip treatment.
 * Mirrors the API's lib/status.ts tables (stage labels/groups, payment
 * groups); everything the UI shows goes through this file. Chip color rules:
 * brand green = done/positive, accent = in flight, amber = needs your
 * attention, rust = declined/expired, paper/bone = inert.
 */

// ---------------------------------------------------------------------------
// Order stages (Opportunity StageName — the SAP sales order lifecycle)
// ---------------------------------------------------------------------------

/** The Hub-submitted draft stage — purchase order requests, never orders. */
export const PURCHASE_REQUEST_STAGE = 'Customer Submitted'

const STAGE_LABELS: Record<string, string> = {
  'Customer Submitted': 'Submitted for review',
  'Open': 'Confirmed',
  'Released': 'Processing',
  'Awaiting Production': 'Scheduled for production',
  'In Production': 'In production',
  'In Testing': 'Quality testing',
  'Blocked / On Hold': 'On hold',
  'Closed Won': 'Complete',
  'Closed Lost': 'Cancelled',
}

const CLOSED_STAGES = ['Closed Won', 'Closed Lost'] as const

/** Client-facing label for a raw stage. Unknown raw values pass through untouched. */
export function stageLabel(stage: string): string {
  return STAGE_LABELS[stage] ?? stage
}

export function stageGroup(stage: string): StageGroup {
  if (stage === PURCHASE_REQUEST_STAGE) return 'request'
  return (CLOSED_STAGES as readonly string[]).includes(stage) ? 'closed' : 'open'
}

const STAGE_CHIP_CLASSES: Record<string, string> = {
  'Customer Submitted': 'bg-amber/15 text-amber border-amber/40',
  'Open': 'bg-accentTint/60 text-accentSoft border-accentSoft/30',
  'Released': 'bg-accentTint text-accent border-accent/30',
  'Awaiting Production': 'bg-accentTint/60 text-accentSoft border-accentSoft/30',
  'In Production': 'bg-accentTint text-accent border-accent/30',
  'In Testing': 'bg-accentTint/80 text-accentSoft border-accent/20',
  'Blocked / On Hold': 'bg-paper text-inkMid border-rule/80',
  'Closed Won': 'bg-brandTint text-brandDeep border-brand/40',
  'Closed Lost': 'bg-paper text-mute border-ruleSoft',
}

const UNKNOWN_CHIP = 'bg-bone/60 text-inkMid border-rule/80'

export function stageChipClasses(stage: string): string {
  return STAGE_CHIP_CLASSES[stage] ?? UNKNOWN_CHIP
}

// ---------------------------------------------------------------------------
// Per-line fulfillment (Apex board DTO `status` values)
// ---------------------------------------------------------------------------

const FULFILLMENT_LABELS: Record<string, string> = {
  awaitingProduction: 'Awaiting production',
  inProduction: 'In production',
  inTesting: 'Quality testing',
  inStock: 'In stock',
  awaitingStock: 'Awaiting stock',
  partiallyShipped: 'Partially shipped',
  fulfilled: 'Fulfilled',
}

const FULFILLMENT_CHIP_CLASSES: Record<string, string> = {
  awaitingProduction: 'bg-accentTint/60 text-accentSoft border-accentSoft/30',
  inProduction: 'bg-accentTint text-accent border-accent/30',
  inTesting: 'bg-accentTint/80 text-accentSoft border-accent/20',
  inStock: 'bg-brandTint text-brandDeep border-brand/30',
  awaitingStock: 'bg-paper text-inkMid border-rule/80',
  partiallyShipped: 'bg-accentTint text-accent border-accent/30',
  fulfilled: 'bg-brandTint text-brandDeep border-brand/40',
}

/** Null for unknown DTO statuses — callers omit the chip rather than guess. */
export function fulfillmentChip(status: string): { label: string; className: string } | null {
  const label = FULFILLMENT_LABELS[status]
  return label ? { label, className: FULFILLMENT_CHIP_CLASSES[status] ?? UNKNOWN_CHIP } : null
}

// ---------------------------------------------------------------------------
// Purchase-request lifecycle (submitted / accepted / declined)
// ---------------------------------------------------------------------------

export const PURCHASE_REQUEST_STATUS_LABELS: Record<PurchaseRequestStatus, string> = {
  submitted: 'In review',
  accepted: 'Accepted',
  declined: 'Declined',
}

const PURCHASE_REQUEST_CHIP_CLASSES: Record<PurchaseRequestStatus, string> = {
  submitted: 'bg-amber/15 text-amber border-amber/40',
  accepted: 'bg-brandTint text-brandDeep border-brand/30',
  declined: 'bg-rust/10 text-rust border-rust/40',
}

export function purchaseRequestChipClasses(status: PurchaseRequestStatus): string {
  return PURCHASE_REQUEST_CHIP_CLASSES[status]
}

// ---------------------------------------------------------------------------
// Requests (Case-backed) — raw Case Status values from the org's CaseStatus
// standard value set. The API derives statusLabel/statusGroup/waitingOnYou
// server-side; the mirror here exists for the mocks and for chip colors.
// ---------------------------------------------------------------------------

export const CASE_STATUS_LABELS: Record<string, string> = {
  'New': 'Received',
  'In Progress': 'In progress',
  'Waiting on Customer': 'Waiting on you',
  'Waiting on Supplier': 'Waiting on supplier',
  'Closed': 'Closed',
}

/** Client-facing label for a raw case status. Unknown raw values pass through untouched. */
export function caseStatusLabel(status: string): string {
  return CASE_STATUS_LABELS[status] ?? status
}

/** Closed is the only closed status; anything unknown stays visible as open. */
export function caseStatusGroup(status: string): CaseStatusGroup {
  return status === 'Closed' ? 'closed' : 'open'
}

/** The one pinning status — the list floats + ambers these, nothing else. */
export function isWaitingOnYou(status: string): boolean {
  return status === 'Waiting on Customer'
}

// Amber is reserved for waiting-on-you — every other status stays off the
// needs-your-attention channel by design.
const CASE_CHIP_CLASSES: Record<string, string> = {
  'New': 'bg-accentTint/60 text-accentSoft border-accentSoft/30',
  'In Progress': 'bg-accentTint text-accent border-accent/30',
  'Waiting on Customer': 'bg-amber/15 text-amber border-amber/40',
  'Waiting on Supplier': 'bg-paper text-inkMid border-rule/80',
  'Closed': 'bg-brandTint text-brandDeep border-brand/40',
}

export function caseChipClasses(status: string): string {
  return CASE_CHIP_CLASSES[status] ?? UNKNOWN_CHIP
}

/** Customer-facing names for the portal form types (the create-page cards). */
export const CASE_TYPE_LABELS: Record<CaseRequestType, string> = {
  document_request: 'Documents',
  questionnaire: 'Questionnaire',
  quality_issue: 'Quality issue',
  technical: 'Technical question',
  regulatory: 'Regulatory',
  other: 'Something else',
}

// Read-only slugs the API emits for staff-created cases — the portal can't
// create these, but lists and details must still name them.
const READONLY_CASE_TYPE_LABELS: Record<string, string> = {
  pricing: 'Pricing & quotes',
  sample: 'Samples',
}

/**
 * Label for ANY Request_Type__c slug the API passes through — the picklist is
 * broader than the portal form types (read-only staff slugs, future values),
 * so unknowns fall back to a neutral 'Request' rather than a blank cell.
 */
export function caseTypeLabel(type: string): string {
  return (
    (CASE_TYPE_LABELS as Record<string, string>)[type] ??
    READONLY_CASE_TYPE_LABELS[type] ??
    'Request'
  )
}

// ---------------------------------------------------------------------------
// Invoice payment status — the API derives the group + label server-side;
// the mirror here exists for the mocks and for chip colors only.
// ---------------------------------------------------------------------------

export function paymentGroup(
  paymentStatus: string | null,
  balanceDue: number | null,
  dueDate: string | null,
): PaymentGroup {
  const balance = balanceDue ?? 0
  if (paymentStatus === 'Paid' || balance <= 0) return 'paid'
  if (dueDate && new Date(dueDate).getTime() < Date.now()) return 'overdue'
  return 'open'
}

export function paymentLabel(group: PaymentGroup, paymentStatus: string | null): string {
  if (group === 'paid') return 'Paid'
  if (group === 'overdue') return paymentStatus === 'Partial' ? 'Partially paid — overdue' : 'Overdue'
  return paymentStatus === 'Partial' ? 'Partially paid' : 'Open'
}

const PAYMENT_CHIP_CLASSES: Record<PaymentGroup, string> = {
  open: 'bg-accentTint/60 text-accentSoft border-accentSoft/30',
  overdue: 'bg-amber/15 text-amber border-amber/40',
  paid: 'bg-brandTint text-brandDeep border-brand/30',
}

export function paymentChipClasses(group: PaymentGroup): string {
  return PAYMENT_CHIP_CLASSES[group]
}

// ---------------------------------------------------------------------------
// Quote status (SAP_Quote__c.Status__c)
// ---------------------------------------------------------------------------

const QUOTE_CHIP_CLASSES: Record<string, string> = {
  draft: 'bg-bone/60 text-inkMid border-rule/80',
  sent: 'bg-accentTint text-accent border-accent/30',
  open: 'bg-accentTint text-accent border-accent/30',
  accepted: 'bg-brandTint text-brandDeep border-brand/30',
  expired: 'bg-rust/10 text-rust border-rust/40',
  cancelled: 'bg-paper text-mute border-ruleSoft',
  canceled: 'bg-paper text-mute border-ruleSoft',
}

export function quoteChipClasses(status: string | null): string {
  return QUOTE_CHIP_CLASSES[(status ?? '').trim().toLowerCase()] ?? UNKNOWN_CHIP
}

/** A quote past its expiration date reads as expired whatever the raw status says. */
export function quoteIsExpired(expirationDate: string | null, status: string | null): boolean {
  if ((status ?? '').trim().toLowerCase() === 'expired') return true
  if (!expirationDate) return false
  const d = new Date(expirationDate)
  return !Number.isNaN(d.getTime()) && d.getTime() < Date.now()
}

// ---------------------------------------------------------------------------
// Credit application (Credit_Application__c.Status__c — raw picklist values)
// ---------------------------------------------------------------------------

const CREDIT_APP_STATUS_LABELS: Record<string, string> = {
  'Draft': 'Draft',
  'Submitted': 'Submitted',
  'References Requested': 'References requested',
  'Under Review': 'Under review',
  'Approved': 'Approved',
  'Declined': 'Declined',
}

/** Client-facing label for a raw application status. Unknown values pass through. */
export function creditAppStatusLabel(status: string): string {
  return CREDIT_APP_STATUS_LABELS[status] ?? status
}

const CREDIT_APP_CHIP_CLASSES: Record<string, string> = {
  'Draft': 'bg-bone/60 text-inkMid border-rule/80',
  'Submitted': 'bg-accentTint text-accent border-accent/30',
  'References Requested': 'bg-accentTint/60 text-accentSoft border-accentSoft/30',
  'Under Review': 'bg-amber/15 text-amber border-amber/40',
  'Approved': 'bg-brandTint text-brandDeep border-brand/40',
  'Declined': 'bg-rust/10 text-rust border-rust/40',
}

export function creditAppChipClasses(status: string): string {
  return CREDIT_APP_CHIP_CLASSES[status] ?? UNKNOWN_CHIP
}

// ---------------------------------------------------------------------------
// Trade reference request (Trade_Reference__c.Request_Status__c)
// ---------------------------------------------------------------------------

const REFERENCE_STATUS_LABELS: Record<string, string> = {
  Pending: 'Waiting to send',
  Sent: 'Sent',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
}

export function referenceStatusLabel(status: string): string {
  return REFERENCE_STATUS_LABELS[status] ?? status
}

const REFERENCE_CHIP_CLASSES: Record<string, string> = {
  Pending: 'bg-bone/60 text-inkMid border-rule/80',
  Sent: 'bg-accentTint text-accent border-accent/30',
  Completed: 'bg-brandTint text-brandDeep border-brand/30',
  Cancelled: 'bg-bone/60 text-mute border-ruleSoft',
}

export function referenceChipClasses(status: string): string {
  return REFERENCE_CHIP_CLASSES[status] ?? UNKNOWN_CHIP
}

// ---------------------------------------------------------------------------
// Stock badge (wizard product search)
// ---------------------------------------------------------------------------

export const STOCK_BADGE_LABELS = {
  in_stock: 'In stock',
  made_to_order: 'Made to order',
} as const

export const STOCK_BADGE_CLASSES = {
  in_stock: 'bg-brandTint text-brandDeep border-brand/30',
  made_to_order: 'bg-accentTint/60 text-accentSoft border-accentSoft/30',
} as const
